import { useMemo } from 'react';
import { computeRecurrenceRange, expandRecurringEvents } from '../recurrence';
import {
  parseContactFilterId,
  buildCalendarIdToAccountId,
  computeHideXaviorEvents,
  filterCalendarEvents,
  computeModuleSyncError,
  computeConnectedEmptyVariant,
  dedupeCrossAccountEvents,
  type ConnectedEmptyVariant,
  type ModuleSyncError,
} from '../lib/calendarEventFilters';
import type { CalendarAccount } from './useCalendarSync';

/**
 * Event-data derivation pipeline extracted from EventListModule (#3279/#3277).
 *
 * Owns the `useMemo` cluster that turns the raw `allEvents` query result + the
 * calendar-account state into the rendered event list and empty-state
 * diagnosis:
 *   - `contactFilterId`        (deps: [filterText])
 *   - `calendarIdToAccountId`  (deps: [accountCalendars])
 *   - `hideXaviorEvents`       (deps: [calendarAccounts, accountCalendars])
 *   - `events`                 (deps: [allEvents, contactFilterId, filterText,
 *                                moduleId, recentlyUncheckedCalendarIds,
 *                                hideXaviorEvents, calendarScope,
 *                                calendarIdToAccountId, calendarAccounts])
 *   - `expandedEvents`         (deps: [events])
 *   - `moduleSyncError`        (deps: [calendarAccounts])
 *   - `connectedEmptyVariant`  (deps: [moduleSyncError, calendarAccounts,
 *                                accountCalendars])
 *
 * Behavior preservation:
 * - Each memo's body is delegated to a pure helper in `lib/calendarEventFilters`
 *   (pinned by `calendarEventFilters.char.test.ts`), and the DEPENDENCY ARRAYS
 *   are byte-for-byte the same as the inline memos — including the now-inert
 *   `module.id`/`filterText` entries the `events` memo carried (they remain so
 *   memo-identity churn is identical to before).
 * - `expandedEvents` still calls `expandRecurringEvents(events, computeRecurrence
 *   Range(new Date()))` with the real current time, exactly as inline.
 * - Hook order/count is fixed: this is called ONCE in the orchestrator, after
 *   the Google-sync hook + `allEvents` are available (same position the inline
 *   memos occupied).
 */
export interface UseCalendarEventDataOptions {
  allEvents: any[];
  filterText?: string | null;
  moduleId: string;
  calendarScope: string;
  calendarAccounts: CalendarAccount[];
  accountCalendars: Record<string, { calendars: Array<{ id: string; name: string; primary: boolean; accessRole: string; color: string | null; selected: boolean }>; loading: boolean; error: string | null }>;
  recentlyUncheckedCalendarIds: Set<string>;
}

export interface UseCalendarEventDataResult {
  events: any[];
  expandedEvents: any[];
  moduleSyncError: ModuleSyncError | null;
  connectedEmptyVariant: ConnectedEmptyVariant;
}

export function useCalendarEventData(opts: UseCalendarEventDataOptions): UseCalendarEventDataResult {
  const {
    allEvents,
    filterText,
    moduleId,
    calendarScope,
    calendarAccounts,
    accountCalendars,
    recentlyUncheckedCalendarIds,
  } = opts;

  const contactFilterId = useMemo(
    () => parseContactFilterId(filterText),
    [filterText],
  );

  // #2726: calendarId → accountId map for scope-filtering.
  const calendarIdToAccountId = useMemo(
    () => buildCalendarIdToAccountId(accountCalendars),
    [accountCalendars],
  );

  // #2724: gate Xavior events on the first-enabled account's primary calendar.
  const hideXaviorEvents = useMemo(
    () => computeHideXaviorEvents(calendarAccounts, accountCalendars),
    [calendarAccounts, accountCalendars],
  );

  // Events are already filtered by moduleId on the backend via module_links JOIN.
  const events = useMemo(
    () => filterCalendarEvents({
      allEvents,
      hideXaviorEvents,
      calendarScope,
      calendarAccounts,
      calendarIdToAccountId,
      recentlyUncheckedCalendarIds,
      contactFilterId,
      filterText,
    }),
    // NOTE: deps mirror the original inline `events` memo byte-for-byte,
    // including the inert `moduleId`/`filterText` entries it carried, so
    // memo-recompute cadence is identical. (The repo's eslint config does not
    // register react-hooks/exhaustive-deps, matching the sibling calendar hooks.)
    [allEvents, contactFilterId, filterText, moduleId, recentlyUncheckedCalendarIds, hideXaviorEvents, calendarScope, calendarIdToAccountId, calendarAccounts],
  );

  // Expand recurring events into virtual instances for the visible range
  // (3 months back, 12 months forward) using the real current time.
  // Expand first, THEN collapse cross-account duplicates (the same event synced
  // from two connected accounts renders twice). Dedup runs post-expansion so
  // recurring copies anchored on different dates per account merge on the shared
  // occurrence, while distinct occurrences of one series stay separate.
  const expandedEvents = useMemo(
    () => dedupeCrossAccountEvents(
      expandRecurringEvents(events, computeRecurrenceRange(new Date())),
    ),
    [events],
  );

  // Module-wide sync error: only when EVERY enabled account is in the same state.
  const moduleSyncError = useMemo(
    () => computeModuleSyncError(calendarAccounts),
    [calendarAccounts],
  );

  // Connected-but-empty surface discriminator.
  const connectedEmptyVariant = useMemo<ConnectedEmptyVariant>(
    () => computeConnectedEmptyVariant(moduleSyncError, calendarAccounts, accountCalendars),
    [moduleSyncError, calendarAccounts, accountCalendars],
  );

  return { events, expandedEvents, moduleSyncError, connectedEmptyVariant };
}
